import { signedFetch } from '../../super-picker/services/api';

const SUPER_CALCULATOR_BASE = '/api/super-calculator';

export interface CalculatorProfile {
  Id: number;
  UserId: string;
  Name: string;
  BirthYear?: number | null;
  MonthlyIncome?: number | null;
  OtherAnnualIncome?: number | null;
  Saving?: number | null;
  Cpf?: number | null;
  CarLoan?: number | null;
  HousingLoan?: number | null;
  CreditCard?: number | null;
  OtherLoan?: number | null;
  SelfEmployedInd?: number | null;
  Citizenship?: string | null;
  /** 1 = eligible for FTA ABSD remission (SC-equivalent rates); 0 = not eligible. Always 0 for SC. */
  FtaNationalityInd?: number | null;
  /** 1 = first property, 2 = second, 3 = third or more. 0 = legacy/unset (treat as 1 in UI). */
  PropertiesOwned?: number | null;
  Remarks?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
  DefunctAt?: string | null;
}

export interface CalculatorProfileGroup {
  Id: number;
  UserId: string;
  Name: string;
  Relationship?: string | null;
  Remarks?: string | null;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
  DefunctAt?: string | null;
  profiles?: CalculatorProfile[];
}

export interface CreateProfileRequest {
  Name: string;
  BirthYear?: number | null;
  MonthlyIncome?: number | null;
  OtherAnnualIncome?: number | null;
  Saving?: number | null;
  Cpf?: number | null;
  CarLoan?: number | null;
  HousingLoan?: number | null;
  CreditCard?: number | null;
  OtherLoan?: number | null;
  SelfEmployedInd?: number;
  Citizenship?: string;
  FtaNationalityInd?: number;
  PropertiesOwned?: number;
  Remarks?: string | null;
}

export interface UpdateProfileRequest {
  Name?: string;
  BirthYear?: number | null;
  MonthlyIncome?: number | null;
  OtherAnnualIncome?: number | null;
  Saving?: number | null;
  Cpf?: number | null;
  CarLoan?: number | null;
  HousingLoan?: number | null;
  CreditCard?: number | null;
  OtherLoan?: number | null;
  SelfEmployedInd?: number;
  Citizenship?: string;
  FtaNationalityInd?: number;
  PropertiesOwned?: number;
  Remarks?: string | null;
}

export interface CreateGroupRequest {
  Name: string;
  Relationship?: string;
  Remarks?: string | null;
  ProfileIds?: number[];
}

export interface UpdateGroupRequest {
  Name?: string;
  Relationship?: string;
  Remarks?: string | null;
  ProfileIds?: number[];
}

export interface ProfilesResponse {
  profiles: CalculatorProfile[];
  groups: CalculatorProfileGroup[];
}

async function requestJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await signedFetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = typeof errorData?.error === 'string' ? errorData.error : 'Request failed';
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const getAllProfiles = async (): Promise<ProfilesResponse> =>
  requestJson<ProfilesResponse>(`${SUPER_CALCULATOR_BASE}/profiles`);

export const createProfile = async (data: CreateProfileRequest): Promise<CalculatorProfile> =>
  requestJson<CalculatorProfile>(`${SUPER_CALCULATOR_BASE}/profiles/profiles`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export const getProfile = async (id: number): Promise<CalculatorProfile> =>
  requestJson<CalculatorProfile>(`${SUPER_CALCULATOR_BASE}/profiles/profiles/${id}`);

export const updateProfile = async (id: number, data: UpdateProfileRequest): Promise<CalculatorProfile> =>
  requestJson<CalculatorProfile>(`${SUPER_CALCULATOR_BASE}/profiles/profiles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });

export const deleteProfile = async (id: number): Promise<void> => {
  await requestJson<{ success: boolean }>(`${SUPER_CALCULATOR_BASE}/profiles/profiles/${id}`, {
    method: 'DELETE',
  });
};

export const createGroup = async (data: CreateGroupRequest): Promise<CalculatorProfileGroup> => {
  const response = await requestJson<{ group: CalculatorProfileGroup; profiles: CalculatorProfile[] }>(
    `${SUPER_CALCULATOR_BASE}/profiles/groups`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
  return {
    ...response.group,
    profiles: response.profiles,
  };
};

export const getGroup = async (id: number): Promise<CalculatorProfileGroup> => {
  const response = await requestJson<{ group: CalculatorProfileGroup; profiles: CalculatorProfile[] }>(
    `${SUPER_CALCULATOR_BASE}/profiles/groups/${id}`
  );
  return {
    ...response.group,
    profiles: response.profiles,
  };
};

export const updateGroup = async (id: number, data: UpdateGroupRequest): Promise<CalculatorProfileGroup> => {
  const response = await requestJson<{ group: CalculatorProfileGroup; profiles: CalculatorProfile[] }>(
    `${SUPER_CALCULATOR_BASE}/profiles/groups/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
  return {
    ...response.group,
    profiles: response.profiles,
  };
};

export const deleteGroup = async (id: number): Promise<void> => {
  await requestJson<{ success: boolean }>(`${SUPER_CALCULATOR_BASE}/profiles/groups/${id}`, {
    method: 'DELETE',
  });
};

export const addProfileToGroup = async (groupId: number, profileId: number): Promise<void> => {
  await requestJson<{ Id: number }>(`${SUPER_CALCULATOR_BASE}/profiles/groups/${groupId}/profiles/${profileId}`, {
    method: 'POST',
  });
};

export const removeProfileFromGroup = async (groupId: number, profileId: number): Promise<void> => {
  await requestJson<{ success: boolean }>(`${SUPER_CALCULATOR_BASE}/profiles/groups/${groupId}/profiles/${profileId}`, {
    method: 'DELETE',
  });
};
